import { FetchArgs, FetchBaseQueryError, QueryStatus } from '@reduxjs/toolkit/query'

import { addMonths, selectedPeriodFor } from '../../features/market-analytics/components/helpers/trendsSelectedPeriod'
import {
  buildConventionalScopeBody,
  ConventionalScope,
  conventionalScopeKey,
  coreApi,
  BLUEPRINT_FAMILY_KEEP_UNUSED_DATA_FOR_SECONDS,
  BlueprintFamilyMonthlyTrendsItem,
  BlueprintFamilyTrendsWindowDays,
} from '../core'
import { chunkIds, createConcurrencyLimiter, TRENDS_MAX_CONCURRENT_REQUESTS, TRENDS_MAX_IDS_PER_REQUEST } from './chunkedRequests'

// Single item in the batch trends response (one per requested blueprint id): the shared
// monthly batched-trends item, where `id` is a blueprint-family id.
//
// Note the response can be SHORTER than the id list sent — see `BlueprintFamilyMonthlyTrendsItem`
// on why a matched-nothing id is omitted rather than zero-filled. The chunking `queryFn`
// below turns that omission into an explicit cached `null`.
export type BlueprintFamilyTrendsBatchApiItem = BlueprintFamilyMonthlyTrendsItem

// Query args for POST /blueprint-families/trends/batch. `ids` must hold 1–10 existing blueprint
// ids; `conventionalSubgenreIds` is an optional body list scoping the adoption denominator.
export type GetBlueprintFamilyTrendsBatchQueryArgs = {
  ids: string[]
  windowDays?: BlueprintFamilyTrendsWindowDays
} & ConventionalScope

const trendsBatchApi = coreApi.injectEndpoints({
  endpoints: (builder) => ({
    // POST-with-body modeled as a `builder.query` (see `getEstimatesList` in core.ts) so the
    // adoption/density series participate in RTK Query caching like the other read endpoints.
    getBlueprintFamilyTrendsBatch: builder.query<BlueprintFamilyTrendsBatchApiItem[], GetBlueprintFamilyTrendsBatchQueryArgs>({
      query: ({ ids, windowDays, ...scope }) => ({
        url: 'blueprint-families/trends/batch',
        method: 'POST',
        params: {
          ...(windowDays !== undefined ? { windowDays } : {}),
        },
        body: { ids, ...buildConventionalScopeBody(scope) },
      }),
      providesTags: (_result, _err, { ids, windowDays, ...scope }) => [
        {
          type: 'BlueprintFamilies',
          id: `trends/batch/${[...ids].sort().join(',')}/${windowDays ?? 'default'}/${conventionalScopeKey(scope)}`,
        },
      ],
    }),
  }),
})

// Representative trend scalars for one blueprint, boiled down from the batch
// response's monthly series so the ranked list can show one number per row.
// `density` is the last point of the per-adopting-game `density` series (events running
// at once per day per game that ran the entity, over the period's last month), or null
// when the backend omits density entirely (no game in the segment).
// `activeEvents` is the period mean of `activeLiveEventsCount` (live event
// instances running at the same time across the segment), null when the series
// is absent or empty. Both ride on the same request, so whichever lens asked
// first pays for both.
export type BlueprintFamilyDensityScalar = {
  id: string
  density: number | null
  activeEvents: number | null
  // The density of the selected period's first and last month, the two ends the ranked
  // list's Change column compares on the density lens. Absent when the request carried no
  // window or the series lacks either month (see `densityChangeOf`).
  densityChange?: DensityChange
  // Set when this id's trends/batch chunk failed while others succeeded: the nulls
  // above then mean "unknown", not "no data in scope". Such ids are never cached, so
  // `refetch()` retries them. A page should render them as unavailable (with a retry),
  // not as a dash sorted to the bottom. The query only errors when every id failed.
  failed?: true
}

export type GetBlueprintFamilyDensityScalarsQueryArgs = {
  ids: string[]
  windowDays?: BlueprintFamilyTrendsWindowDays
} & ConventionalScope

// The two months of a density series the Change column compares: the selected period's
// first and last complete months, the same pair the adoption Change reads off the adoption
// series. Values are events per adopting game, as plotted.
export type DensityChange = {
  first: number
  last: number
}

// First- and last-month density of the selected period, or undefined when either month is
// not in the series. A series that starts later (the entity or the panel did not exist
// yet) or stops earlier has no like-for-like pair, so there is no change to show rather
// than one measured between other months than the hover names. Unlike `densityScalarOf`
// this reads the two months by their key, not by position.
export function densityChangeOf(
  item: Pick<BlueprintFamilyTrendsBatchApiItem, 'density'>,
  windowDays: BlueprintFamilyTrendsWindowDays | undefined,
  today: Date = new Date()
): DensityChange | undefined {
  const period = selectedPeriodFor(windowDays, today)
  if (!period || !item.density) return undefined
  const lastMonth = addMonths(period.inProgressMonth, -1)
  const first = item.density.find((point) => point.month === period.startMonth)
  const last = item.density.find((point) => point.month === lastMonth)
  if (!first || !last) return undefined
  return { first: first.value, last: last.value }
}

// Pulls the representative density scalar off a trends item: the period's last month,
// the same figure the detail pages' Density tile shows and every trend chart ends on.
export function densityScalarOf(item: Pick<BlueprintFamilyTrendsBatchApiItem, 'density'>): number | null {
  const series = item.density
  if (!series || series.length === 0) return null
  return series[series.length - 1].value
}

// "yyyy-MM" of the UTC month `date` falls in; the backend's months are UTC.
export function utcMonthKey(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`
}

// Period mean of the concurrent-instances series. The backend ships no scalar for this
// series, so the mean is taken here over the period's months, which are all complete.
//
// An in-progress month (only an older backend returns one) is excluded, because the
// chart excludes it too (`keepsPartialTailForMetric('activeEvents')` is false): it is
// averaged over however many days the month has actually run, so a month three days old
// drags the mean toward a figure the market never ran at. Months are UTC, the backend's.
export function activeEventsScalarOf(item: Pick<BlueprintFamilyTrendsBatchApiItem, 'activeLiveEventsCount'>, today: Date = new Date()): number | null {
  const series = item.activeLiveEventsCount
  if (!series || series.length === 0) return null

  const currentMonth = utcMonthKey(today)
  // Never average nothing: a series that is only the in-progress month still has to
  // report something, and one partial month beats a dash.
  const complete = series.filter((point) => point.month < currentMonth)
  const points = complete.length > 0 ? complete : series

  let sum = 0
  for (const point of points) sum += point.value
  return sum / points.length
}

type CachedScalars = Omit<BlueprintFamilyDensityScalar, 'id' | 'failed'>

// Module-level scalar cache keyed by id + window + scope + UTC month, so a filter/chip
// change that only adds/removes a few leaves does not re-download the series for ids
// we already boiled down. `null` fields are valid cached misses (blueprint has no
// density / no instances in this scope).
//
// The month is in the key because the selected period is whole calendar months ending
// before the current one: every scalar moves on the 1st, and a tab left open across it
// must not keep last month's figures. Only successful chunks are written, so ids from a
// failed chunk are retried by the next fetch. The cache is cleared by
// `resetBlueprintFamilyDensityScalarCache` (internal blueprint family edits) and
// whenever RTK holds no fulfilled density entry anymore (all expired, or
// `resetApiState`), and bypassed by forced fetches (`refetch()`, tag invalidation).
const densityScalarCache = new Map<string, CachedScalars>()

type ScalarOutcome = { scalars: CachedScalars } | { error: FetchBaseQueryError }

// Per-key outcomes of chunks still in flight, so a query whose ids overlap one already
// being fetched waits for it instead of requesting the same ids again.
const inFlightScalars = new Map<string, Promise<ScalarOutcome>>()

// Bumped by every reset: a chunk that lands after one must not write its figures back
// into the cleared cache.
let densityScalarCacheGeneration = 0

export function densityScalarCacheKey(
  id: string,
  windowDays: BlueprintFamilyTrendsWindowDays | undefined,
  scopeKey: string,
  monthKey: string = utcMonthKey(new Date())
): string {
  return `${id}/${windowDays ?? 'default'}/${scopeKey}/${monthKey}`
}

export function resetBlueprintFamilyDensityScalarCache(): void {
  densityScalarCache.clear()
  inFlightScalars.clear()
  densityScalarCacheGeneration += 1
}

export function readDensityScalarCache(ids: string[], keyOf: (id: string) => string): { missingIds: string[] } {
  const missingIds: string[] = []
  for (const id of ids) {
    if (!densityScalarCache.has(keyOf(id))) missingIds.push(id)
  }
  return { missingIds }
}

export function writeDensityScalarCache(items: BlueprintFamilyDensityScalar[], keyOf: (id: string) => string): void {
  for (const { id, failed, ...scalars } of items) {
    if (!failed) densityScalarCache.set(keyOf(id), scalars)
  }
}

export function densityScalarsFromCache(ids: string[], keyOf: (id: string) => string): BlueprintFamilyDensityScalar[] {
  return ids.map((id) => {
    const cached = densityScalarCache.get(keyOf(id))
    return {
      id,
      density: cached?.density ?? null,
      activeEvents: cached?.activeEvents ?? null,
      ...(cached?.densityChange && { densityChange: cached.densityChange }),
    }
  })
}

type ChunkResponse = { data?: unknown; error?: FetchBaseQueryError }

// What one trends/batch chunk says about each of its ids. An id whose scope matches no
// tracked game comes back absent from a SUCCESSFUL chunk rather than zero-filled, and
// resolves to null scalars (a real, cacheable "no data"); every id of a failed chunk
// carries the chunk's error instead.
export function densityOutcomesOfChunk(
  chunk: string[],
  response: ChunkResponse,
  today: Date = new Date(),
  windowDays?: BlueprintFamilyTrendsWindowDays
): Map<string, ScalarOutcome> {
  const outcomes = new Map<string, ScalarOutcome>()
  if (response.error) {
    const { error } = response
    chunk.forEach((id) => outcomes.set(id, { error }))
    return outcomes
  }
  const items = (response.data as BlueprintFamilyTrendsBatchApiItem[] | undefined) ?? []
  const byId = new Map(items.map((item) => [item.id, item]))
  chunk.forEach((id) => {
    const item = byId.get(id)
    const densityChange = item ? densityChangeOf(item, windowDays, today) : undefined
    outcomes.set(id, {
      scalars: {
        density: item ? densityScalarOf(item) : null,
        activeEvents: item ? activeEventsScalarOf(item, today) : null,
        ...(densityChange && { densityChange }),
      },
    })
  })
  return outcomes
}

// Merges per-id outcomes into the query result: an error only when every id failed, so
// one bad chunk can't blank the whole column; otherwise failed ids are flagged `failed`.
export function assembleDensityScalars(
  ids: string[],
  outcomes: Map<string, ScalarOutcome>
): { data: BlueprintFamilyDensityScalar[]; error?: undefined } | { error: FetchBaseQueryError; data?: undefined } {
  let firstError: FetchBaseQueryError | undefined
  let anySucceeded = false
  const data = ids.map((id): BlueprintFamilyDensityScalar => {
    const outcome = outcomes.get(id)
    if (outcome && 'scalars' in outcome) {
      anySucceeded = true
      return { id, ...outcome.scalars }
    }
    if (outcome && !firstError) firstError = outcome.error
    return { id, density: null, activeEvents: null, failed: true }
  })
  if (!anySucceeded && firstError) return { error: firstError }
  return { data }
}

function toFetchError(error: unknown): FetchBaseQueryError {
  return { status: 'FETCH_ERROR', error: String(error) }
}

type FetchWithBaseQuery = (args: FetchArgs) => ChunkResponse | PromiseLike<ChunkResponse>

// The density-scalars queryFn body, kept free of RTK plumbing so it can be tested with a
// stubbed `fetchWithBQ`. Missing ids are chunked into ≤10-id trends/batch requests, at
// most `TRENDS_MAX_CONCURRENT_REQUESTS` in flight at a time.
export async function fetchDensityScalars(
  { ids, windowDays, ...scope }: GetBlueprintFamilyDensityScalarsQueryArgs,
  fetchWithBQ: FetchWithBaseQuery,
  { forced = false, now = new Date() }: { forced?: boolean; now?: Date } = {}
): Promise<{ data: BlueprintFamilyDensityScalar[]; error?: undefined } | { error: FetchBaseQueryError; data?: undefined }> {
  if (ids.length === 0) return { data: [] }

  const scopeKey = conventionalScopeKey(scope)
  const monthKey = utcMonthKey(now)
  const keyOf = (id: string) => densityScalarCacheKey(id, windowDays, scopeKey, monthKey)
  const generation = densityScalarCacheGeneration

  const pending = new Map<string, Promise<ScalarOutcome>>()
  const toFetch: string[] = []
  Array.from(new Set(ids)).forEach((id) => {
    const key = keyOf(id)
    const cached = forced ? undefined : densityScalarCache.get(key)
    const inFlight = forced ? undefined : inFlightScalars.get(key)
    if (cached) pending.set(id, Promise.resolve({ scalars: cached }))
    else if (inFlight) pending.set(id, inFlight)
    else toFetch.push(id)
  })

  const schedule = createConcurrencyLimiter(TRENDS_MAX_CONCURRENT_REQUESTS)
  chunkIds(toFetch, TRENDS_MAX_IDS_PER_REQUEST).forEach((chunk) => {
    const chunkOutcomes = schedule(() =>
      Promise.resolve(
        fetchWithBQ({
          url: 'blueprint-families/trends/batch',
          method: 'POST',
          params: {
            ...(windowDays !== undefined ? { windowDays } : {}),
          },
          body: { ids: chunk, ...buildConventionalScopeBody(scope) },
        })
      )
    )
      .then(
        (response) => densityOutcomesOfChunk(chunk, response, now, windowDays),
        (error: unknown) => densityOutcomesOfChunk(chunk, { error: toFetchError(error) }, now, windowDays)
      )
      .then((outcomes) => {
        chunk.forEach((id) => {
          const key = keyOf(id)
          const outcome = outcomes.get(id)
          if (generation === densityScalarCacheGeneration && outcome && 'scalars' in outcome) densityScalarCache.set(key, outcome.scalars)
          if (inFlightScalars.get(key) === pending.get(id)) inFlightScalars.delete(key)
        })
        return outcomes
      })

    chunk.forEach((id) => {
      const outcome = chunkOutcomes.then((outcomes) => outcomes.get(id) ?? { error: toFetchError('missing chunk outcome') })
      pending.set(id, outcome)
      inFlightScalars.set(keyOf(id), outcome)
    })
  })

  const settled = new Map<string, ScalarOutcome>()
  await Promise.all(Array.from(pending.entries()).map(async ([id, outcome]) => settled.set(id, await outcome)))

  return assembleDensityScalars(ids, settled)
}

// True while RTK still holds a fulfilled density-scalars entry. Once none is left (every
// entry expired after `keepUnusedDataFor`, or `resetApiState` ran on a language change
// or sign-in), the module cache has outlived what it was caching for.
function hasFulfilledDensityEntry(state: unknown): boolean {
  const apiState = (state as Record<string, { queries?: Record<string, { endpointName?: string; status?: QueryStatus } | undefined> } | undefined>)[
    coreApi.reducerPath
  ]
  return Object.values(apiState?.queries ?? {}).some(
    (entry) => entry?.endpointName === 'getBlueprintFamilyDensityScalars' && entry.status === QueryStatus.fulfilled
  )
}

const densityScalarsApi = coreApi.injectEndpoints({
  endpoints: (builder) => ({
    // Windowed density + concurrent-instances scalars per blueprint for the
    // whole ranked list (one request serves both non-adoption lenses). The
    // catalogue can hold more than the batch endpoint's 10-id cap, so ids are
    // chunked into ≤10-id requests and merged. We only keep the scalar (not the
    // full monthly series) so the cache entry stays small even for large lists.
    // Scalars are also kept in a module map keyed per id so a later query with
    // a different id set only fetches the missing ids. See `fetchDensityScalars`.
    getBlueprintFamilyDensityScalars: builder.query<BlueprintFamilyDensityScalar[], GetBlueprintFamilyDensityScalarsQueryArgs>({
      keepUnusedDataFor: BLUEPRINT_FAMILY_KEEP_UNUSED_DATA_FOR_SECONDS,
      async queryFn(args, queryApi, _extraOptions, fetchWithBQ) {
        if (!hasFulfilledDensityEntry(queryApi.getState())) resetBlueprintFamilyDensityScalarCache()
        return fetchDensityScalars(args, fetchWithBQ, { forced: queryApi.forced })
      },
      providesTags: (_result, _err, { windowDays, ...scope }) => [
        {
          type: 'BlueprintFamilies',
          id: `density-scalars/${windowDays ?? 'default'}/${conventionalScopeKey(scope)}`,
        },
      ],
    }),
  }),
})

export const { useGetBlueprintFamilyTrendsBatchQuery } = trendsBatchApi
export const { useGetBlueprintFamilyDensityScalarsQuery } = densityScalarsApi
